import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api, fmtTime, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard } from "../components/common";

const toCsv = (rows) => {
  const head = "Date,Staff,Clock in,Clock out,Break (min),Hours worked";
  const body = rows.map((r) => [r.date, r.staff_name, fmtTime(r.clock_in), fmtTime(r.clock_out), r.break_minutes, r.worked_hours].join(","));
  const blob = new Blob([[head, ...body].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "dexterity-staff-book.csv";
  a.click();
};

export default function Timesheet() {
  const { user } = useAuth();
  const [days, setDays] = useState(14);
  const [staffId, setStaffId] = useState("");
  const [team, setTeam] = useState([]);
  const [data, setData] = useState({ rows: [], total_hours: 0, total_break_minutes: 0 });
  const multi = user.role !== "staff";

  useEffect(() => { if (user.role === "employer") api.get("/employer/team").then((r) => setTeam(r.data)); }, [user.role]);
  useEffect(() => { api.get("/timesheet", { params: { days, staff_id: staffId || undefined } }).then((r) => setData(r.data)); }, [days, staffId]);

  return (
    <div>
      <PageHeader eyebrow="Time keeping" title="Staff book" subtitle="Hours are calculated from photo-verified clock ins, breaks and clock outs."
        action={<button className="dx-btn-ghost" onClick={() => toCsv(data.rows)} disabled={!data.rows.length} data-testid="timesheet-export-btn"><Download className="w-4 h-4" />Export CSV</button>} />
      <div className="flex flex-wrap gap-2 mb-5">
        <select className="dx-input w-auto" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="timesheet-range-select">
          {[7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        {user.role === "employer" && (
          <select className="dx-input w-auto" value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="timesheet-staff-select">
            <option value="">All team members</option>
            {team.map((t) => <option key={t.staff_id} value={t.staff_id}>{t.staff_name}</option>)}
          </select>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard accent label="Hours worked" value={data.total_hours} testid="timesheet-total-hours" />
        <StatCard label="Break minutes" value={data.total_break_minutes} testid="timesheet-total-breaks" />
        <StatCard label="Days recorded" value={data.rows.length} testid="timesheet-days" />
      </div>
      <div className="dx-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="timesheet-table">
          <thead className="bg-[#FAF8FC] text-left text-xs uppercase tracking-wider text-[#8a7a92]">
            <tr>{["Date", ...(multi ? ["Staff"] : []), "Clock in", "Clock out", "Break", "Hours"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6b5a73]">No time records in this period.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.date + r.staff_id} data-testid={`timesheet-row-${r.date}`}>
                <td className="px-4 py-3 font-medium whitespace-nowrap">{fmtDate(r.date)}</td>
                {multi && <td className="px-4 py-3">{r.staff_name}</td>}
                <td className="px-4 py-3">{fmtTime(r.clock_in)}</td>
                <td className="px-4 py-3">{r.open ? <span className="text-emerald-700 font-semibold">On shift</span> : fmtTime(r.clock_out)}</td>
                <td className="px-4 py-3">{r.break_minutes} min</td>
                <td className="px-4 py-3 font-semibold text-[#5B2A6E]">{r.worked_hours} h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
